import { useState, useEffect, useMemo } from 'react';
import {
  decodeOAuthState,
  type RedirectInfo,
} from '../utils/oauth-state-utils';
import { OAUTH_STATE_KEYS } from '../utils/oauth-constants';
import {
  SESSION_STORAGE_ALLOWED_GROUPS,
  LOCAL_STORAGE_SLUG,
  LOCAL_STORAGE_STRIPE_DEEP_LINK,
  routes,
} from '@seaters-app/constants';

interface UseOAuthStateOptions {
  state: string | null;
  provider?: string;
}

interface UseOAuthStateResult {
  parsedState: ReturnType<typeof decodeOAuthState> | null;
  origin: string | undefined;
  stripeDeepLink: string | undefined;
  wlScreenPath: string | null;
  setSlug: (slug: string) => void;
}

/**
 * Custom hook for OAuth state management
 * Handles parsing, storage, and state management for OAuth flows
 */
export const useOAuthState = (
  { state, provider }: UseOAuthStateOptions,
  setSlug: (slug: string) => void
): UseOAuthStateResult => {
  const [origin, setOrigin] = useState<string | undefined>(undefined);
  const [stripeDeepLink, setStripeDeepLink] = useState<string | undefined>(
    undefined
  );
  const [wlScreenPath, setWlScreenPath] = useState<string | null>(null);

  // Memoize parsed state to avoid unnecessary re-parsing
  const parsedState = useMemo(() => {
    if (!state) return null;

    try {
      return decodeOAuthState(state);
    } catch (error) {
      console.error('[OAuth] Error parsing state:', error);
      return null;
    }
  }, [state]);

  // Process redirect info and update local state
  useEffect(() => {
    if (!parsedState) return;

    try {
      const { redirectInfo } = parsedState;

      // Set origin and slug
      const redirectOrigin = redirectInfo[OAUTH_STATE_KEYS.ORIGIN];
      setOrigin(redirectOrigin);

      if (redirectOrigin) {
        setSlug(redirectOrigin);
      }

      // Handle waiting list and survey redirects
      const wlId = redirectInfo[OAUTH_STATE_KEYS.WL_ID];
      const surveyAfterEventToken = redirectInfo[OAUTH_STATE_KEYS.SURVEY_TOKEN];

      if (surveyAfterEventToken) {
        setWlScreenPath(
          `${wlId}/${routes.surveyAfterEvent}?token=${surveyAfterEventToken}`
        );
      } else {
        setWlScreenPath(wlId ?? null);
      }

      // Update localStorage and sessionStorage
      updateStorageWithRedirectInfo(redirectInfo);
    } catch (error) {
      console.error('[OAuth] Error processing redirect info:', error);
    }
  }, [parsedState, setSlug]);

  return {
    parsedState,
    origin,
    stripeDeepLink,
    wlScreenPath,
    setSlug,
  };
};

/**
 * Updates browser storage with redirect information
 */
const updateStorageWithRedirectInfo = (redirectInfo: RedirectInfo): void => {
  try {
    // Update origin in localStorage
    const origin = redirectInfo[OAUTH_STATE_KEYS.ORIGIN];
    if (origin && origin !== localStorage.getItem(LOCAL_STORAGE_SLUG)) {
      localStorage.setItem(LOCAL_STORAGE_SLUG, origin);
    }

    // Update allowed groups in sessionStorage
    const allowedGroups = redirectInfo[OAUTH_STATE_KEYS.ALLOWED_GROUPS];
    if (
      allowedGroups &&
      allowedGroups !== sessionStorage.getItem(SESSION_STORAGE_ALLOWED_GROUPS)
    ) {
      sessionStorage.setItem(SESSION_STORAGE_ALLOWED_GROUPS, allowedGroups);
    }

    // Update stripe deep link in localStorage
    const stripeDeepLink = redirectInfo[OAUTH_STATE_KEYS.STRIPE_DEEP_LINK];
    if (stripeDeepLink) {
      localStorage.setItem(LOCAL_STORAGE_STRIPE_DEEP_LINK, stripeDeepLink);
    }
  } catch (error) {
    console.error('[OAuth] Error updating storage:', error);
  }
};
